import { HttpError } from 'react-admin'
import { stringify } from 'query-string'


export interface CreateParams {
  data?: any
}

/**
 * Rethrows an axios failure as a react-admin HttpError so the error surfaces
 * to the user (notification) and authProvider.checkError can log out on
 * 401/403 (checkError reads `status`). Swallowing errors here renders
 * silently-empty lists/records instead.
 */
export const rethrowHttpError = (e: any): never => {
  const status = e?.response?.status
  const body = e?.response?.data
  const message = typeof body?.errors === 'string' ? body.errors : e?.message
  throw new HttpError(message || 'Request failed', status, body)
}

export interface Headers {
  Authorization?: string
  'Content-Type'?: string
}

/**
 * Sorts and filters an array of data objects based on the provided query's sort field and direction.
 *
 * @param {any[]} data - The array of data objects to be sorted.
 * @param {object} query - The query object containing a JSON string 'sort' property.
 *   The 'sort' property should be a JSON stringified array: [field, direction].
 *   Example: '["name", "ASC"]'
 * @returns {any[]} The sorted array, reversed if direction is 'DESC'.
 *
 * @example
 * const sorted = handleSortAndFilter(users, { sort: '["created_on", "DESC"]' });
 */
export const handleSortAndFilter = (data: any[], query: any): any[] => {
  const { sort } = query;
  const [field, dir] = JSON.parse(sort);

  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: "base",
  });

  data.sort((current, compareTo) => {
    let currentValue = current[field];
    let compareToValue = compareTo[field];

    if (currentValue == null && compareToValue == null) return 0;
    if (currentValue == null) return 1;
    if (compareToValue == null) return -1;

    switch (field) {
      case "created_on":
      case "expires_on":
      case "UserCreateDate": {
        const currentDate = new Date(currentValue).getTime();
        const compareToDate = new Date(compareToValue).getTime();
        return currentDate - compareToDate;
      }
      default: {
        if (typeof currentValue === "string") currentValue = currentValue.trim();
        if (typeof compareToValue === "string") compareToValue = compareToValue.trim();
        return collator.compare(String(currentValue), String(compareToValue));
      }
    }
  });

  return dir === "DESC" ? data.reverse() : data;
}

export const makeHeaders = (token?: string) => ({
  Authorization: `bearer ${token}`,
})

export const makeUrl = (facadeBaseUrl: string = '', url: string = '', query: any = {}) => {
  if (url.indexOf('?') >= 0) {
    return `${facadeBaseUrl}/${url}${stringify(query)}`;  
  }
  return `${facadeBaseUrl}/${url}?${stringify(query)}`;
}

export interface Params {
  id?: string,
  filter: any,
  pagination: any,
  sort: any
  previousData?: any
}

export interface UpdateParams {
  id: string,
  data: any,
  previousData?: any
}

export const replaceUrlParams = (url: string = '', params: any) => {
  let newUrl = url
  Object.keys(params).forEach(key => {
    const value = params[key]
    newUrl = newUrl.replace(`<${key}>`, value)
  })
  return newUrl
}

export const toBoolean = (str: string) => {
  const valid = ['true', 'True']
  if (valid.indexOf(str) >= 0) {
    return true
  }
  return false
}
